
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MFOItem, MFOSubTask } from "@/types/kanban";
import { Calendar, CheckCircle2, Clock, DollarSign, ListChecks, Users, ExternalLink } from "lucide-react";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { updateSubtaskStatus, recalculateAndUpdateMFOProgress, fetchSubtasksForMFO } from "@/utils/supabase/subtaskUtils";
import { Skeleton } from "../ui/skeleton";

// Department names mapping
const DEPARTMENT_NAMES: Record<string, string> = {
  "dept-tourism": "Tourism Division",
  "dept-culture": "Cultural Affairs",
  "dept-events": "Events Management"
};

interface MFODetailDialogProps {
  mfo: MFOItem | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case "delayed":
      return <Clock className="h-4 w-4 text-amber-500" />;
    case "in-progress":
      return <Clock className="h-4 w-4 text-blue-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800";
    case "medium":
      return "bg-amber-100 text-amber-800";
    case "low":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "not-started":
      return "bg-gray-100 text-gray-800";
    case "in-progress":
      return "bg-blue-100 text-blue-800";
    case "delayed":
      return "bg-amber-100 text-amber-800";
    case "completed":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getProgressColor = (progress: number) => {
  if (progress < 30) return "bg-red-500";
  if (progress < 70) return "bg-amber-500";
  return "bg-green-500";
};

const MFODetailDialog = ({ mfo, isOpen, onOpenChange }: MFODetailDialogProps) => {
  const navigate = useNavigate();
  const [subTasks, setSubTasks] = useState<MFOSubTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isOpen && mfo) {
      setProgress(mfo.progress);
      loadSubtasks();
    }
  }, [isOpen, mfo]);

  const loadSubtasks = async () => {
    if (!mfo) return;
    
    setIsLoading(true);
    try {
      const subtasks = await fetchSubtasksForMFO(mfo.id);
      setSubTasks(subtasks);
    } catch (error) {
      console.error("Error loading subtasks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubTaskChange = async (id: string, completed: boolean) => {
    if (!mfo) return;
    
    // Optimistically update UI
    setSubTasks(
      subTasks.map((task) => 
        task.id === id ? { ...task, completed } : task
      )
    );
    
    // Update in database
    const success = await updateSubtaskStatus(id, completed);
    
    if (success) {
      // Recalculate and update MFO progress
      const newProgress = await recalculateAndUpdateMFOProgress(mfo.id);
      
      if (newProgress >= 0) {
        // Update local state with new progress
        setProgress(newProgress);
      }
    } else {
      // Revert optimistic update if failed
      setSubTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === id ? { ...task, completed: !completed } : task
        )
      );
    }
  };

  const handleViewDetails = () => {
    if (mfo) {
      onOpenChange(false);
      navigate(`/dashboard/mfo/${mfo.id}`);
    }
  };

  if (!mfo) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[725px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{mfo.code}</Badge>
            <DialogTitle className="text-xl">{mfo.title}</DialogTitle>
          </div>
          <DialogDescription>
            <div className="flex items-center mt-1 text-sm">
              <Calendar className="h-4 w-4 mr-1" /> 
              Due: {mfo.dueDate}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Status and Tags */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={getStatusColor(mfo.status)}>
              <span className="flex items-center gap-1">
                {getStatusIcon(mfo.status)}
                {mfo.status.replace('-', ' ')}
              </span>
            </Badge>
            <Badge variant="outline" className={getPriorityColor(mfo.priority)}>
              {mfo.priority} priority
            </Badge>
            {Object.entries(mfo.timeline).map(([quarter, isActive]) => 
              isActive && (
                <Badge key={quarter} variant="outline" className="bg-primary/10">
                  {quarter.toUpperCase()}
                </Badge>
              )
            )}
          </div>

          {/* Department */}
          <div>
            <h4 className="text-sm font-medium mb-2">Department</h4>
            <div className="text-sm p-2 bg-muted rounded-md">
              {DEPARTMENT_NAMES[mfo.departmentId] || "Unknown Department"}
            </div>
          </div>

          {/* Success Indicators */}
          <div>
            <h4 className="text-sm font-medium mb-2">Success Indicators</h4>
            <ul className="space-y-2 text-sm">
              {mfo.successIndicators.map((indicator, idx) => (
                <li key={idx} className="flex items-start gap-2 p-2 bg-muted rounded-md">
                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                  <span>{indicator}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Budget */}
          <div>
            <h4 className="text-sm font-medium mb-2">Budget</h4>
            <div className="flex items-center text-sm p-2 bg-muted rounded-md">
              <DollarSign className="h-4 w-4 mr-1 text-primary" />
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'PHP',
                maximumFractionDigits: 0,
              }).format(mfo.budget)}
            </div>
          </div>

          {/* Progress */}
          <div>
            <h4 className="text-sm font-medium mb-2">Progress ({progress}%)</h4>
            <Progress value={progress} className={getProgressColor(progress)} />
          </div>

          {/* Sub-tasks */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <ListChecks className="h-4 w-4 mr-1" /> Sub-tasks
            </h4>
            
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center space-x-2">
                    <Skeleton className="h-4 w-4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {subTasks.length > 0 ? (
                  subTasks.map((task) => (
                    <div key={task.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`dialog-${task.id}`} 
                        checked={task.completed}
                        onCheckedChange={(checked) => 
                          handleSubTaskChange(task.id, checked === true)
                        }
                      />
                      <Label 
                        htmlFor={`dialog-${task.id}`}
                        className={task.completed ? "line-through text-muted-foreground" : ""}
                      >
                        {task.text}
                      </Label>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No subtasks available for this MFO.</p>
                )}
              </div>
            )}
          </div>

          {/* Collaborators */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center">
              <Users className="h-4 w-4 mr-1" /> Collaborators
            </h4>
            <div className="flex flex-wrap gap-2 mt-2">
              {mfo.collaborators && mfo.collaborators.length > 0 ? (
                mfo.collaborators.map((person) => (
                  <div 
                    key={person.id} 
                    className="flex items-center p-2 bg-muted rounded-md text-sm"
                  >
                    <Avatar className="h-6 w-6 mr-2">
                      <AvatarImage src={person.avatar} alt={person.name} />
                      <AvatarFallback className="text-[10px]">
                        {person.name.split(" ").map(n => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar>
                    {person.name}
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No collaborators assigned to this MFO.</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <div className="flex gap-2">
            <Button onClick={handleViewDetails}>
              <ExternalLink className="h-4 w-4 mr-2" />
              View Details
            </Button>
            <Button>Edit MFO</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MFODetailDialog;
